import { ChangeDetectionStrategy, Component, DestroyRef, effect, input, output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { MasterListViewComponent, provideSimpleListView } from '@examdojo/core/list-view';
import { TextInputComponent } from '@examdojo/ui/input';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { ReviewPermissionsAgGridService } from './review-permissions-ag-grid.service';
import { ReviewPermissionsListViewData } from './review-permissions-list-view.model';
import { ReviewPermissionsListViewService } from './review-permissions-list-view.service';

@Component({
  selector: 'exam-review-permissions-list-view',
  templateUrl: './review-permissions-list-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full' },
  styleUrls: ['./review-permissions-list-view.component.scss'],
  imports: [MasterListViewComponent, TextInputComponent, ButtonComponent, IconComponent, ReactiveFormsModule],
  providers: [provideSimpleListView(ReviewPermissionsListViewService), ReviewPermissionsAgGridService],
})
export class ReviewPermissionsListViewComponent {
  constructor(
    private readonly reviewPermissionsListViewService: ReviewPermissionsListViewService,
    private readonly reviewPermissionsAgGridService: ReviewPermissionsAgGridService,
    private readonly destroyRef: DestroyRef,
  ) {
    // Setup search functionality when searchCtrl is available
    effect(() => {
      const searchCtrl = this.searchCtrl();
      if (searchCtrl) {
        searchCtrl.valueChanges
          .pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap((searchTerm: string) => this.setSearchTerm(searchTerm)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }
    });
  }

  // Inputs from parent component
  readonly searchCtrl = input.required<FormControl<string>>();
  readonly isUserAdmin = input.required<boolean>();
  readonly isFormPristine = input.required<boolean>();
  readonly isSaving = input.required<boolean>();

  // Output to parent component
  readonly saveClicked = output<void>();

  @ViewChild(MasterListViewComponent, { static: false })
  masterListView!: MasterListViewComponent;

  readonly state = connectState({
    reviewPermissions: this.reviewPermissionsListViewService.data$,
  });

  readonly columnDefs: Array<ColDef<ReviewPermissionsListViewData>> = this.reviewPermissionsAgGridService.columnDefs;
  readonly gridOptions: GridOptions<ReviewPermissionsListViewData> = this.reviewPermissionsAgGridService.gridOptions;

  private gridApi: GridApi<ReviewPermissionsListViewData> | null = null;

  onGridReady(evt: GridReadyEvent) {
    // Grid will use groupDefaultExpanded: -1 setting to expand all groups by default
    this.gridApi = evt.api;
  }

  // Public method for parent component to control search
  setSearchTerm(searchTerm: string): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', searchTerm || '');
    }
  }

  // Handle save button click
  onSaveClick(): void {
    this.saveClicked.emit();
  }

  // Getter to expose grid API
  get gridAPI(): GridApi<ReviewPermissionsListViewData> | null {
    return this.gridApi;
  }
}
